import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminChangePassword({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setEmail(user?.email || ""); }, [open, user]);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Unesite trenutnu lozinku.");
    if (next && next.length < 6) return toast.error("Nova lozinka mora imati najmanje 6 znakova.");
    if (next && next !== confirm) return toast.error("Lozinke se ne poklapaju.");
    const emailChanged = email && email.toLowerCase() !== (user?.email || "").toLowerCase();
    if (!emailChanged && !next) return toast.error("Nema promjena za sačuvati.");
    setBusy(true);
    try {
      const payload = { current_password: current };
      if (emailChanged) payload.new_email = email.toLowerCase();
      if (next) payload.new_password = next;
      const { data } = await client.post("/auth/change-password", payload);
      if (data.token) localStorage.setItem("melletic_token", data.token);
      await refresh();
      toast.success("Podaci naloga su ažurirani.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium outline-none focus:border-blue-500";
  const labelCls = "text-xs font-semibold text-slate-500 mb-1 block";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-[380px] rounded-3xl border-0 p-0 overflow-hidden bg-white" data-testid="admin-change-password-modal">
        <div className="px-6 pt-7 pb-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600"><KeyRound size={22} /></span>
            <div>
              <h2 className="font-heading text-lg font-extrabold text-slate-900">Podaci naloga</h2>
              <p className="text-sm text-slate-500">Promijeni email i/ili lozinku</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className={labelCls}>Email (korisničko ime)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@melletic.com" className={inputCls} data-testid="cp-email" required />
            </div>
            <div className="pt-1 border-t border-slate-100" />
            <div>
              <label className={labelCls}>Trenutna lozinka (obavezno)</label>
              <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Trenutna lozinka" className={inputCls} data-testid="cp-current" required />
            </div>
            <div>
              <label className={labelCls}>Nova lozinka (opcionalno)</label>
              <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Ostavi prazno ako ne mijenjaš" className={inputCls} data-testid="cp-new" />
            </div>
            {next && (
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Potvrdi novu lozinku" className={inputCls} data-testid="cp-confirm" />
            )}
            <button type="submit" disabled={busy} data-testid="cp-submit"
              className="w-full bg-blue-600 text-white font-bold py-3 rounded-2xl press disabled:opacity-60 flex items-center justify-center gap-2 mt-1">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={17} />} Sačuvaj promjene
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
